/**
 * LeavesAndAbsencesView
 *
 * "Leaves & Absences" page: Congés (sub-view 0) and the arrêts de travail (sub-view 1). On
 * desktop the sub-views are picked from the sidebar group in MenuBar; on a phone the rail is
 * replaced by MobileMenu, which has no sub-items, so a tab bar is shown here.
 *
 * The arrêts sub-view is open to everyone, but what it shows depends on the `workStoppages`
 * scope: its holders (HR, Paie) get the company-wide suivi, everyone else their own arrêts.
 */
import React from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Tabs, Tab } from "@mui/material";
import Langs from "../../app/lang/langs";
import { checkPermission } from "../../app/utils/helpers";
import { config } from "../../app/utils/config";
import { LeavesView } from "./LeavesView";
import WorkStoppagesView from "./WorkStoppagesView";
import MyWorkStoppagesView from "./MyWorkStoppagesView";

export const LEAVES_SUBVIEW = 0;
export const WORK_STOPPAGES_SUBVIEW = 1;

export default function LeavesAndAbsencesView() {
  const urlParams = useParams();
  const navigate = useNavigate();
  const isMobile = useSelector((state) => state.settings.isMobile);
  const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds);
  // Subscribed so the view re-renders once permissions arrive.
  useSelector((state) => state.login.permissions);

  // Its own scope, not `leaves`: the suivi shows every collab's motif d'arrêt company-wide.
  // See permissions.md.
  const hasWorkStoppagesView = checkPermission(config.scopes.workStoppages, config.permissionTypes.view);
  const subView = Number(urlParams.subView) === WORK_STOPPAGES_SUBVIEW ? WORK_STOPPAGES_SUBVIEW : LEAVES_SUBVIEW;

  /**
   * The phone's sub-view switch, handed DOWN to whichever sub-view is showing so it renders
   * inside that view's own top bar.
   *
   * It used to be rendered here, as a sibling above the sub-view. That put it in normal flow
   * at y=0 — directly underneath `.topMenu`, which is `position: fixed; top: 0` with an
   * opaque background and z-index 1000 on phones. The strip was therefore invisible and
   * un-tappable (no way to reach Arrêts de travail at all), while still occupying 41px of
   * document flow that the sub-views' hard-coded top offsets knew nothing about — which is
   * where the blank band under the header and the off-screen pagination came from.
   */
  const subTabs = isMobile === 2 ? (
    <Tabs
      value={subView}
      onChange={(_e, value) => navigate(`/leaves/${selectedTeamsIds[0]}/${value}`)}
      variant="fullWidth"
      sx={{
        minHeight: 40,
        "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 600 },
      }}
    >
      <Tab value={LEAVES_SUBVIEW} label={<Langs str="Leaves" />} />
      <Tab value={WORK_STOPPAGES_SUBVIEW} label={<Langs str="WORK_STOPPAGES" />} />
    </Tabs>
  ) : null;

  /**
   * On a phone the page is one flex column the height of the visual viewport, and the sub-view
   * scrolls inside it — the same shape MobilePlanningView and MobileClientsView already use.
   * Nothing is `position: fixed` here, so no offset has to be guessed: the bar, the tabs and
   * the list simply stack. `dvh` rather than `vh` because the mobile browser's toolbar is not
   * subtracted from `vh`, which is what pushed the pagination row permanently off-screen.
   */
  if (isMobile === 2) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          "@supports (height: 100dvh)": { height: "100dvh" },
          overflow: "hidden",
        }}
      >
        {subView === LEAVES_SUBVIEW && <LeavesView subTabs={subTabs} />}
        {subView === WORK_STOPPAGES_SUBVIEW &&
          (hasWorkStoppagesView ? <WorkStoppagesView subTabs={subTabs} /> : <MyWorkStoppagesView subTabs={subTabs} />)}
      </Box>
    );
  }

  return (
    <div>
      {subView === LEAVES_SUBVIEW && <LeavesView />}
      {subView === WORK_STOPPAGES_SUBVIEW && (hasWorkStoppagesView ? <WorkStoppagesView /> : <MyWorkStoppagesView />)}
    </div>
  );
}
